import { type FC, useState } from "react";

import type { DashboardOrgMcpServer } from "../../dashboard-client";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import {
  draftFromServer,
  emptyDraft,
  slugFromDisplayName,
  useOrgMcpServers,
  type McpHeaderDraft,
  type McpServerDraft,
} from "../../hooks/useOrgMcpServers";
import { isAdmin } from "../../utils/rbac";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { FormCheckbox } from "../common/forms/FormCheckbox";
import { FormInput } from "../common/forms/FormInput";
import { FormMessage } from "../common/forms/FormMessage";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";

const LABEL =
  "mb-1 block font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400";
const HINT =
  "mt-1 font-mono text-[10px] leading-snug text-slate-500 dark:text-slate-400";

/**
 * The header editor. No component like it existed, and a JSON textarea would
 * have been the cheap answer -- but these values are credentials, and a textarea
 * shows them in the clear, cannot mark one secret, and turns a typo into a parse
 * error instead of a field-level complaint.
 */
const HeadersEditor: FC<{
  headers: McpHeaderDraft[];
  onChange: (headers: McpHeaderDraft[]) => void;
}> = ({ headers, onChange }) => {
  const patch = (index: number, changes: Partial<McpHeaderDraft>) => {
    onChange(
      headers.map((header, i) =>
        i === index ? { ...header, ...changes } : header,
      ),
    );
  };

  return (
    <div>
      <Text as="span" className={LABEL}>
        Headers
      </Text>
      {headers.length === 0 && (
        <p className={HINT}>
          No headers. Most hosted MCP servers authenticate with one or two.
        </p>
      )}
      <div className="space-y-1.5">
        {headers.map((header, index) => (
          <div
            key={index}
            className="border border-slate-300 p-2 dark:border-slate-700"
          >
            <div className="flex items-start gap-2">
              <div className="w-2/5 shrink-0">
                <input
                  aria-label={`Header ${index + 1} name`}
                  className="w-full rounded-none border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-900 focus:border-emerald-500 focus:outline-none dark:border-slate-700 dark:bg-arti-background dark:text-slate-100"
                  onChange={(event) =>
                    patch(index, { name: event.target.value })
                  }
                  placeholder="DD_API_KEY"
                  value={header.name}
                />
              </div>
              <div className="min-w-0 grow">
                {header.isSecret &&
                header.storedValueSet &&
                header.value.length === 0 ? (
                  <StoredHeaderValue
                    onReplace={() => patch(index, { value: " " })}
                  />
                ) : (
                  <input
                    aria-label={`Header ${index + 1} value`}
                    autoComplete="off"
                    className="w-full rounded-none border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-900 focus:border-emerald-500 focus:outline-none dark:border-slate-700 dark:bg-arti-background dark:text-slate-100"
                    onChange={(event) =>
                      patch(index, { value: event.target.value })
                    }
                    placeholder={header.isSecret ? "paste the secret" : "value"}
                    type={header.isSecret ? "password" : "text"}
                    value={
                      header.value.trim() === ""
                        ? header.value.trim()
                        : header.value
                    }
                  />
                )}
              </div>
              <button
                className="shrink-0 py-1 font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400"
                onClick={() => onChange(headers.filter((_, i) => i !== index))}
                type="button"
              >
                remove
              </button>
            </div>
            <FormCheckbox
              checked={header.isSecret}
              className="mt-1.5"
              label="Secret"
              onChange={(checked) =>
                patch(index, {
                  // Flipping to readable cannot inherit a value nobody can
                  // read, and flipping to secret must not leave the old value
                  // sitting in a readable column.
                  isSecret: checked,
                  storedValueSet: false,
                  value: "",
                })
              }
            />
          </div>
        ))}
      </div>
      <button
        className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-slate-500 hover:text-emerald-600 dark:text-slate-400 dark:hover:text-emerald-400"
        onClick={() =>
          onChange([
            ...headers,
            { isSecret: true, name: "", storedValueSet: false, value: "" },
          ])
        }
        type="button"
      >
        + add header
      </button>
    </div>
  );
};

function submitLabel(saving: boolean, editing: boolean): string {
  if (saving) {
    return "Saving…";
  }
  return editing ? "Save changes" : "Register server";
}

const ServerForm: FC<{
  draft: McpServerDraft;
  editing: boolean;
  error: string | null;
  onCancel: () => void;
  onChange: (draft: McpServerDraft) => void;
  onSubmit: () => void;
  saving: boolean;
}> = ({ draft, editing, error, onCancel, onChange, onSubmit, saving }) => {
  const slugTouched =
    editing || draft.slug !== slugFromDisplayName(draft.displayName);

  return (
    <form
      className="space-y-3 border border-slate-300 bg-slate-50/50 p-3 dark:border-slate-700 dark:bg-slate-950/30"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <FormInput
          label="Name"
          onChange={(event) =>
            onChange({
              ...draft,
              displayName: event.target.value,
              slug: slugTouched
                ? draft.slug
                : slugFromDisplayName(event.target.value),
            })
          }
          placeholder="Datadog"
          required
          value={draft.displayName}
        />
        <FormInput
          disabled={editing}
          helperText={
            editing
              ? "Fixed after registration — agents refer to tools by it."
              : "Prefixes this server's tools for agents. Lowercase and underscores."
          }
          label="Reference"
          onChange={(event) => onChange({ ...draft, slug: event.target.value })}
          placeholder="datadog"
          required
          value={draft.slug}
        />
      </div>

      <div>
        <Text as="span" className={LABEL}>
          URL
        </Text>
        {/*
          A textarea, not an input, because these URLs carry configuration: the
          Datadog one selects toolsets and omits individual tools in its query
          string. In a single-line field an admin can only ever see the first
          fifty characters of a decision they are making. Newlines are stripped,
          so it stays a URL.
        */}
        <textarea
          className="w-full break-all rounded-none border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-900 focus:border-emerald-500 focus:outline-none dark:border-slate-700 dark:bg-arti-background dark:text-slate-100"
          onChange={(event) =>
            onChange({ ...draft, url: event.target.value.replace(/\s+/gu, "") })
          }
          placeholder="https://mcp.example.com/mcp?toolsets=core"
          required
          rows={3}
          value={draft.url}
        />
        <p className={HINT}>
          HTTP transport only. Query parameters are sent exactly as written.
        </p>
      </div>

      <FormInput
        label="Description"
        onChange={(event) =>
          onChange({ ...draft, description: event.target.value })
        }
        placeholder="What this server is for (optional)"
        value={draft.description}
      />

      <HeadersEditor
        headers={draft.headers}
        onChange={(headers) => onChange({ ...draft, headers })}
      />

      <FormCheckbox
        checked={draft.status === "active"}
        description="Disabled servers stay registered but cannot be attached."
        label="Available to attach"
        onChange={(checked) =>
          onChange({ ...draft, status: checked ? "active" : "disabled" })
        }
      />

      {error && <FormMessage type="error">{error}</FormMessage>}

      <div className="flex items-center gap-2">
        <Button disabled={saving} size="sm" type="submit" variant="primary">
          {submitLabel(saving, editing)}
        </Button>
        <Button onClick={onCancel} size="sm" type="button" variant="secondary">
          Cancel
        </Button>
      </div>
    </form>
  );
};

/**
 * What a stored header's value looks like in the list: a secret says only that
 * it is set, and an unset one says so plainly instead of looking identical to a
 * set one, because "saved" and "empty" are different problems.
 */
function headerValueSummary(
  header: DashboardOrgMcpServer["headers"][number],
): string {
  if (!header.is_secret) {
    return `: ${header.value ?? ""}`;
  }
  return header.value_set ? " ••••" : " unset";
}

const ServerRow: FC<{
  onDelete: () => void;
  onEdit: () => void;
  server: DashboardOrgMcpServer;
}> = ({ onDelete, onEdit, server }) => {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="border border-slate-300 bg-gradient-to-r from-slate-100 via-white to-slate-50 p-3 dark:border-slate-700 dark:from-slate-900/80 dark:via-slate-950/30 dark:to-slate-950/40">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs font-bold text-slate-900 dark:text-slate-100">
          {server.display_name}
        </span>
        <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          {server.slug}
        </span>
        {server.status === "disabled" && (
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-amber-600 dark:text-amber-500">
            disabled
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-3 divide-x divide-slate-300 dark:divide-slate-700">
          <button
            className="font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500 hover:text-sky-600 dark:text-slate-400 dark:hover:text-sky-400"
            onClick={onEdit}
            type="button"
          >
            edit
          </button>
          {confirming ? (
            <>
              <button
                className="font-mono text-[9px] uppercase tracking-[0.1em] text-rose-600 hover:text-rose-500 dark:text-rose-400"
                onClick={onDelete}
                type="button"
              >
                confirm
              </button>
              <button
                className="font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500 hover:text-slate-400"
                onClick={() => setConfirming(false)}
                type="button"
              >
                cancel
              </button>
            </>
          ) : (
            <button
              className="font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400"
              onClick={() => setConfirming(true)}
              type="button"
            >
              remove
            </button>
          )}
        </div>
      </div>
      {server.description && (
        <p className="mt-1 font-mono text-[10px] text-slate-500 dark:text-slate-400">
          {server.description}
        </p>
      )}
      {/* The whole URL, wrapped, in the inset box this app uses for literal
          strings. Truncating it would hide the query string, which is where the
          tool selection lives. */}
      <p className="mt-1.5 break-all border border-slate-200 bg-white/90 px-2 py-1 font-mono text-[10px] text-slate-700 dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-300">
        {server.url}
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        {server.headers.length === 0 ? (
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">
            no headers
          </span>
        ) : (
          server.headers.map((header) => (
            <span
              className="font-mono text-[9px] text-slate-500 dark:text-slate-400"
              key={header.name}
            >
              {header.name}
              <span className="text-slate-400 dark:text-slate-600">
                {headerValueSummary(header)}
              </span>
            </span>
          ))
        )}
      </div>
    </div>
  );
};

export const McpServersPanel: FC = () => {
  const auth = useDashboardAuth();
  const canManage = isAdmin(auth.orgRole, auth.globalRole);
  const {
    servers: loadedServers,
    loading,
    error,
    create,
    remove,
    update,
  } = useOrgMcpServers(auth.organizationId);
  // Never let a bad payload reach a `.length` in render: a settings tab has no
  // business being able to unmount the dashboard.
  const servers = Array.isArray(loadedServers) ? loadedServers : [];
  const [draft, setDraft] = useState<McpServerDraft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!auth.organizationId) {
    return (
      <EmptyState
        description="Select an organization to manage its MCP servers."
        title="No organization selected"
      />
    );
  }

  if (!canManage) {
    return (
      <Alert variant="info">
        MCP servers are managed by an organization admin. Registering one gives
        every actor in this organization a new outbound connection using the
        organization&apos;s credentials.
      </Alert>
    );
  }

  if (loading && servers.length === 0) {
    return <LoadingState message="Loading MCP servers..." />;
  }

  const submit = async () => {
    if (!draft) {
      return;
    }
    setSaving(true);
    const failure = editingId
      ? await update(editingId, draft)
      : await create(draft);
    setSaving(false);
    if (failure) {
      setFormError(failure);
      return;
    }
    setDraft(null);
    setEditingId(null);
    setFormError(null);
  };

  return (
    <PanelSection title="MCP Servers">
      <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
        Tool servers this organization can attach to actor templates and to
        individual actors. Header values are stored encrypted and are never
        shown again after saving.
      </p>

      {error && (
        <div className="mb-3">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <div className="space-y-2">
        {servers.length === 0 && !draft && (
          <EmptyState
            description="Register one to give this organization's actors tools the platform does not ship."
            title="No MCP servers yet"
          />
        )}
        {servers.map((server) =>
          editingId === server.id && draft ? (
            <ServerForm
              draft={draft}
              editing
              error={formError}
              key={server.id}
              onCancel={() => {
                setDraft(null);
                setEditingId(null);
                setFormError(null);
              }}
              onChange={setDraft}
              onSubmit={() => void submit()}
              saving={saving}
            />
          ) : (
            <ServerRow
              key={server.id}
              onDelete={() => void remove(server.id)}
              onEdit={() => {
                setDraft(draftFromServer(server));
                setEditingId(server.id);
                setFormError(null);
              }}
              server={server}
            />
          ),
        )}
      </div>

      {draft && !editingId && (
        <div className="mt-2">
          <ServerForm
            draft={draft}
            editing={false}
            error={formError}
            onCancel={() => {
              setDraft(null);
              setFormError(null);
            }}
            onChange={setDraft}
            onSubmit={() => void submit()}
            saving={saving}
          />
        </div>
      )}

      {!draft && (
        <div className="mt-3">
          <Button
            onClick={() => {
              setDraft(emptyDraft());
              setEditingId(null);
              setFormError(null);
            }}
            size="sm"
            variant="primary"
          >
            Add MCP server
          </Button>
        </div>
      )}
    </PanelSection>
  );
};

interface StoredHeaderValueProps {
  onReplace: () => void;
}

function StoredHeaderValue({ onReplace }: StoredHeaderValueProps) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
        ••••••••••••
      </span>
      <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-500">
        stored
      </span>
      <button
        className="ml-auto font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500 hover:text-sky-600 dark:text-slate-400 dark:hover:text-sky-400"
        onClick={onReplace}
        type="button"
      >
        replace
      </button>
    </div>
  );
}
